import { useEffect, useRef } from 'react';
import axios from 'axios';
import { getToken } from '../services/DaoService';

const API_URL = import.meta.env.VITE_APP_API_URL;

// Convert a base64url VAPID public key to a Uint8Array expected by the browser
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function fetchVapidPublicKey() {
  const res = await axios.get(`${API_URL}/vapid-public-key`, {
    headers: { Authorization: `Bearer ${getToken()}`, Accept: 'application/json' },
  });
  return res.data.data;
}

async function sendSubscriptionToServer(subscription) {
  const json = subscription.toJSON();
  await axios.post(
    `${API_URL}/push-subscription`,
    {
      endpoint: json.endpoint,
      keys: { auth: json.keys.auth, p256dh: json.keys.p256dh },
    },
    {
      headers: { Authorization: `Bearer ${getToken()}`, Accept: 'application/json' },
    }
  );
}

async function removeSubscriptionFromServer(endpoint) {
  await axios.delete(`${API_URL}/push-subscription`, {
    data: { endpoint },
    headers: { Authorization: `Bearer ${getToken()}`, Accept: 'application/json' },
  });
}

export function usePushSubscription(user) {
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!user || subscribedRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    subscribedRef.current = true;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;

        // Check existing subscription
        let subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          // Re-sync with server in case it was lost
          await sendSubscriptionToServer(subscription);
          return;
        }

        // Ask for permission only if not already granted/denied
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const vapidKey = await fetchVapidPublicKey();
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });

        await sendSubscriptionToServer(subscription);
      } catch (err) {
        console.error('[Push] Subscription failed:', err);
        subscribedRef.current = false;
      }
    })();

    return () => {
      // On logout, unsubscribe
      navigator.serviceWorker.ready.then(async (reg) => {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await removeSubscriptionFromServer(sub.endpoint);
          await sub.unsubscribe();
        }
      });
    };
  }, [user]);
}
